import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  ChatMessage,
  ChatbotConfig,
  ChatbotResponse,
  Conversation,
  ConversationHistoryResponse,
} from '../types';

export interface ChatbotState {
  // Mensajes
  messages: ChatMessage[];
  currentConversationId: string | null;
  
  // Configuración UI
  config: ChatbotConfig;
  
  // Estados de carga
  isLoading: boolean;
  isSending: boolean;
  
  // Errores
  error: string | null;
  
  // Historial de conversaciones
  conversations: Conversation[];
  isHistoryLoading: boolean;
  isHistoryOpen: boolean;
  currentConversationTitle: string;
  
  // WebSocket
  isWebSocketConnected: boolean;
  
  // Metadata de la última respuesta AI
  lastResponseMetadata?: {
    intent?: string;
    intentConfidence?: number;
    model?: string;
    provider?: string;
    tokensUsed?: number;
    processingTime?: number;
  };
}

const initialState: ChatbotState = {
  messages: [],
  currentConversationId: null,
  config: {
    isOpen: false,
    position: 'bottom-right',
    minimized: false,
  },
  isLoading: false,
  isSending: false,
  error: null,
  conversations: [],
  isHistoryLoading: false,
  isHistoryOpen: false,
  currentConversationTitle: 'Nueva Conversación',
  isWebSocketConnected: false,
};

const chatbotSlice = createSlice({
  name: 'chatbot',
  initialState,
  reducers: {
    // ========== UI Actions ==========
    toggleChat: (state) => {
      state.config.isOpen = !state.config.isOpen;
      state.config.minimized = false;
    },
    minimizeChat: (state) => {
      state.config.minimized = !state.config.minimized;
    },
    closeChat: (state) => {
      state.config.isOpen = false;
      state.config.minimized = false;
    },
    setChatPosition: (state, action: PayloadAction<ChatbotConfig['position']>) => {
      state.config.position = action.payload;
    },
    
    // ========== Message Actions ==========
    sendMessageRequest: (state, action: PayloadAction<{ message: string; conversationId?: string }>) => {
      const userMessage: ChatMessage = {
        id: `msg-${Date.now()}`,
        content: action.payload.message,
        role: 'user',
        timestamp: new Date().toISOString(), // Convert to ISO string for Redux serialization
      };
      state.messages.push(userMessage);
      state.isSending = true;
      state.error = null;
      
      // Agregar mensaje de carga del asistente
      const loadingMessage: ChatMessage = {
        id: `msg-loading-${Date.now()}`,
        content: '',
        role: 'assistant',
        timestamp: new Date().toISOString(), // Convert to ISO string
        isLoading: true,
      };
      state.messages.push(loadingMessage);
    },
    sendMessageSuccess: (state, action: PayloadAction<ChatbotResponse & { metadata?: any }>) => {
      state.isSending = false;
      state.error = null;
      
      // Remover mensaje de carga
      const loadingIndex = state.messages.findIndex(m => m.isLoading);
      if (loadingIndex !== -1) {
        state.messages.splice(loadingIndex, 1);
      }
      
      // Agregar respuesta del asistente
      const assistantMessage: ChatMessage = {
        id: `msg-${Date.now()}`,
        content: action.payload.message,
        role: 'assistant',
        timestamp: action.payload.timestamp instanceof Date 
          ? action.payload.timestamp.toISOString() 
          : typeof action.payload.timestamp === 'string' 
            ? action.payload.timestamp 
            : new Date(action.payload.timestamp).toISOString(),
      };
      state.messages.push(assistantMessage);
      
      // Actualizar conversationId si es nueva
      if (!state.currentConversationId) {
        state.currentConversationId = action.payload.conversationId;
      }
      
      // Guardar metadata
      if (action.payload.metadata) {
        state.lastResponseMetadata = action.payload.metadata;
      }
    },
    sendMessageFailure: (state, action: PayloadAction<string>) => {
      state.isSending = false;
      state.error = action.payload;
      
      // Remover mensaje de carga
      const loadingIndex = state.messages.findIndex(m => m.isLoading);
      if (loadingIndex !== -1) {
        state.messages.splice(loadingIndex, 1);
      }
    },
    
    // Recibir mensaje vía WebSocket (streaming)
    receiveMessageChunk: (state, action: PayloadAction<{ chunk: string; conversationId: string }>) => {
      // Actualizar el último mensaje del asistente con el chunk
      const lastMessage = state.messages[state.messages.length - 1];
      if (lastMessage && lastMessage.role === 'assistant' && lastMessage.isLoading) {
        lastMessage.content += action.payload.chunk;
      }
    },
    receiveMessageComplete: (state, action: PayloadAction<ChatbotResponse & { metadata?: any }>) => {
      // Marcar el último mensaje como completo
      const lastMessage = state.messages[state.messages.length - 1];
      if (lastMessage && lastMessage.isLoading) {
        lastMessage.content = action.payload.message;
        lastMessage.isLoading = false;
        // Convert timestamp to ISO string for Redux serialization
        lastMessage.timestamp = action.payload.timestamp instanceof Date 
          ? action.payload.timestamp.toISOString() 
          : typeof action.payload.timestamp === 'string' 
            ? action.payload.timestamp 
            : new Date(action.payload.timestamp).toISOString();
      }
      
      if (action.payload.metadata) {
        state.lastResponseMetadata = action.payload.metadata;
      }
    },
    
    // ========== Conversation Actions ==========
    createConversationRequest: (state) => {
      state.isLoading = true;
    },
    createConversationSuccess: (state, action: PayloadAction<{ conversationId: string; title?: string }>) => {
      state.isLoading = false;
      state.currentConversationId = action.payload.conversationId;
      state.currentConversationTitle = action.payload.title || 'Nueva Conversación';
      state.messages = [];
    },
    createConversationFailure: (state, action: PayloadAction<string>) => {
      state.isLoading = false;
      state.error = action.payload;
    },
    
    // ========== History Actions ==========
    fetchConversationsRequest: (state, _action: PayloadAction<{ page?: number; limit?: number; search?: string } | undefined>) => {
      state.isHistoryLoading = true;
    },
    fetchConversationsSuccess: (state, action: PayloadAction<ConversationHistoryResponse>) => {
      state.isHistoryLoading = false;
      state.conversations = action.payload.data;
    },
    fetchConversationsFailure: (state, action: PayloadAction<string>) => {
      state.isHistoryLoading = false;
      state.error = action.payload;
    },
    
    selectConversation: (state, action: PayloadAction<Conversation>) => {
      state.currentConversationId = action.payload.id;
      state.currentConversationTitle = action.payload.title;
      state.messages = []; // Se cargarán desde la saga
      state.isHistoryOpen = false;
    },
    
    loadConversationMessagesRequest: (state, _action: PayloadAction<string>) => {
      state.isLoading = true;
    },
    loadConversationMessagesSuccess: (state, action: PayloadAction<ChatMessage[]>) => {
      state.isLoading = false;
      state.messages = action.payload;
    },
    loadConversationMessagesFailure: (state, action: PayloadAction<string>) => {
      state.isLoading = false;
      state.error = action.payload;
    },
    
    renameConversationRequest: (state, _action: PayloadAction<{ conversationId: string; newTitle: string }>) => {
      // Optimistic update
      const conv = state.conversations.find(c => c.id === _action.payload.conversationId || c.conversationId === _action.payload.conversationId);
      if (conv) {
        conv.title = _action.payload.newTitle;
        conv.name = _action.payload.newTitle; // Para compatibilidad
      }
      if (state.currentConversationId === _action.payload.conversationId) {
        state.currentConversationTitle = _action.payload.newTitle;
      }
    },
    renameConversationSuccess: (state, action: PayloadAction<{ conversationId: string; newTitle: string }>) => {
      const conv = state.conversations.find(c => c.id === action.payload.conversationId || c.conversationId === action.payload.conversationId);
      if (conv) {
        conv.title = action.payload.newTitle;
        conv.name = action.payload.newTitle; // Para compatibilidad
      }
      if (state.currentConversationId === action.payload.conversationId) {
        state.currentConversationTitle = action.payload.newTitle;
      }
    },
    renameConversationFailure: (state, action: PayloadAction<{ conversationId: string; error: string }>) => {
      state.error = action.payload.error;
      // Revertir cambio optimista
      const conv = state.conversations.find(c => c.id === action.payload.conversationId || c.conversationId === action.payload.conversationId);
      if (conv) {
        // Recargar desde el servidor
        state.isHistoryLoading = true;
      }
    },
    
    deleteConversationRequest: (state, _action: PayloadAction<string>) => {
      // Optimistic update
      state.conversations = state.conversations.filter(c => c.id !== _action.payload);
      if (state.currentConversationId === _action.payload) {
        state.currentConversationId = null;
        state.messages = [];
        state.currentConversationTitle = 'Nueva Conversación';
      }
    },
    deleteConversationSuccess: (state) => {
      // Ya se hizo el update optimista
    },
    deleteConversationFailure: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
      // Recargar conversaciones
      state.isHistoryLoading = true;
    },
    
    // ========== History UI ==========
    openHistory: (state) => {
      state.isHistoryOpen = true;
    },
    closeHistory: (state) => {
      state.isHistoryOpen = false;
    },
    
    // ========== WebSocket ==========
    setWebSocketConnected: (state, action: PayloadAction<boolean>) => {
      state.isWebSocketConnected = action.payload;
    },
    
    // ========== Utility ==========
    clearMessages: (state) => {
      state.messages = [];
      state.currentConversationId = null;
      state.currentConversationTitle = 'Nueva Conversación';
    },
    clearError: (state) => {
      state.error = null;
    },
    resetChatbot: (state) => {
      return initialState;
    },
  },
});

export const {
  // UI
  toggleChat,
  minimizeChat,
  closeChat,
  setChatPosition,
  
  // Messages
  sendMessageRequest,
  sendMessageSuccess,
  sendMessageFailure,
  receiveMessageChunk,
  receiveMessageComplete,
  
  // Conversations
  createConversationRequest,
  createConversationSuccess,
  createConversationFailure,
  
  // History
  fetchConversationsRequest,
  fetchConversationsSuccess,
  fetchConversationsFailure,
  selectConversation,
  loadConversationMessagesRequest,
  loadConversationMessagesSuccess,
  loadConversationMessagesFailure,
  renameConversationRequest,
  renameConversationSuccess,
  renameConversationFailure,
  deleteConversationRequest,
  deleteConversationSuccess,
  deleteConversationFailure,
  
  // History UI
  openHistory,
  closeHistory,
  
  // WebSocket
  setWebSocketConnected,
  
  // Utility
  clearMessages,
  clearError,
  resetChatbot,
} = chatbotSlice.actions;

export default chatbotSlice.reducer;

